import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import Form from 'jl-design-system/components/form/Form';
import { Message } from 'jl-design-system/components/message/Message';
import { SecondaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { ClickAndCollectSearchProps } from './ClickAndCollectSearch.types';

// Config
import clickAndCollectSearchState from './ClickAndCollectSearch.state';
import { useWindowSize } from '@context/window/WindowSizeContext';
import {
  findCollectionPoint,
  openSavedCollectionPointsModal,
} from '@redux/actions/click-and-collect/clickAndCollectActions';
import errorMessageBodyLookup from '@utils/error/errorMessageBodyLookup';
import clickAndCollectSearchFormConfig from '@utils/form/configs/clickAndCollect';

// Components
import Container from '@components/container';
import GetGeoLocation from '@components/get-geo-location';

// Styles
import styles from './click-and-collect-search.scss';

const ClickAndCollectSearch = ({
  clickAndCollectCollectionPointFieldLabel = '',
  collectionPointSearchTerm = '',
  disabled = false,
  findCollectionPointError,
}: ClickAndCollectSearchProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    collectionPoints = [],
    geoLocationSearchActive,
    isLoadingLazyComponent,
  } = useSelector(clickAndCollectSearchState);
  const { desktopResolution } = useWindowSize();

  const onClickHandler = () => dispatch(openSavedCollectionPointsModal());
  const onSubmitHandler = (formData: { collectionPointSearchTerm: string }) => {
    const { collectionPointSearchTerm } = formData;
    return dispatch(findCollectionPoint(collectionPointSearchTerm));
  };

  const getSavedCollectionPointsModalButton = () => ({
    extra: true,
    name: 'saved-collection-points-modal-button',
    component: SecondaryButton,
    props: {
      children: 'View recent collection points',
      className: cx('mb-0', {
        'ml-2': desktopResolution,
        'mt-2': !desktopResolution,
      }),
      'data-testid': 'saved-collection-points-modal-button',
      inline: desktopResolution,
      onClick: onClickHandler,
      submitting: isLoadingLazyComponent,
    },
  });

  const collectionPointsAvailable = collectionPoints.length > 0;
  const savedCollectionPointsModalButton = collectionPointsAvailable ? getSavedCollectionPointsModalButton() : null;
  const formConfig = clickAndCollectSearchFormConfig({
    savedCollectionPointsModalButton,
    clickAndCollectCollectionPointFieldLabel,
  });

  return (
    <Container className={styles.container} width="full">
      {findCollectionPointError && (
        <Container marginBottom="2">
          <Message
            body={errorMessageBodyLookup({
              bodyLookup: findCollectionPointError?.code,
              body: findCollectionPointError?.body,
            })}
            dataTestId="click-collect-points-not-found-message"
            title={findCollectionPointError.message}
            type="error"
          />
        </Container>
      )}

      <Form
        config={formConfig}
        data-testid="click-collect-address-search-form"
        destroyOnUnmount={false}
        disabled={disabled || geoLocationSearchActive}
        form={formConfig.id}
        initialValues={{ collectionPointSearchTerm }}
        multiFormValidation
        onSubmit={onSubmitHandler}
        useFormValidationErrorsAlertMessage={false}
      />

      <Container marginBottom="2">
        <GetGeoLocation disabled={disabled} />
      </Container>
    </Container>
  );
};

export default ClickAndCollectSearch;
